import React, { useEffect, useRef, useState } from 'react';
import { createRegistrationOrder, captureRegistrationOrder, fetchPayPalClientId } from '../../services/api';
import { calculateFeeBreakdown } from '../../utils/registrationFee';

const feeTypes = [
  { key: 'standard', label: 'Standard' },
  { key: 'student', label: 'Student' }
];

function loadPayPalSdk({ clientId, currency }) {
  return new Promise((resolve, reject) => {
    try {
      const trimmed = (clientId || '').trim();
      if (!trimmed) return reject(new Error('Empty PayPal client ID'));
      
      const requestedCurrency = currency || 'USD';
      
      // Check if SDK is already loaded with the correct currency
      if (window.paypal && window.paypal.Buttons) {
        const existing = document.querySelector('script[data-paypal-sdk]');
        const loadedCurrency = existing?.getAttribute('data-paypal-currency');
        if (loadedCurrency === requestedCurrency) {
          return resolve(window.paypal);
        }
      }

      // Check if script exists
      let script = document.querySelector('script[data-paypal-sdk]');
      const scriptCurrency = script?.getAttribute('data-paypal-currency');
      
      // If script exists but currency doesn't match, remove it and force reload
      if (script && scriptCurrency !== requestedCurrency) {
        script.remove();
        delete window.paypal;
        script = null;
      }

      const params = new URLSearchParams({
        'client-id': trimmed,
        currency: requestedCurrency,
        intent: 'capture',
        components: 'buttons'
      });
      const sdkUrl = `https://www.paypal.com/sdk/js?${params.toString()}`;
      
      // If script exists with same currency, wait for it or use it
      if (script && scriptCurrency === requestedCurrency) {
        if (window.paypal && window.paypal.Buttons) {
          return resolve(window.paypal);
        }
        // Script exists but not fully loaded yet, wait for it
        let settled = false;
        const onLoad = () => { if (settled) return; settled = true; resolve(window.paypal); };
        script.addEventListener('load', onLoad, { once: true });
        setTimeout(() => {
          if (!settled && window.paypal && window.paypal.Buttons) {
            settled = true;
            resolve(window.paypal);
          }
        }, 3000);
        return;
      }

      // Create new script
      script = document.createElement('script');
      script.src = sdkUrl;
      script.setAttribute('data-paypal-sdk', 'true');
      script.setAttribute('data-paypal-currency', requestedCurrency);
      
      let settled = false;
      script.onload = () => {
        if (settled) return;
        settled = true;
        resolve(window.paypal);
      };
      script.onerror = () => {
        if (settled) return;
        settled = true;
        reject(new Error('Failed to load PayPal SDK'));
      };
      
      document.body.appendChild(script);
    } catch (err) {
      reject(err);
    }
  });
}

export default function AttendeeRegistrationModal({ conference, onClose, onSuccess }) {
  const [step, setStep] = useState(1); // 1 = configuration, 2 = payment
  const [feeType, setFeeType] = useState('standard');
  const [withAccompanied, setWithAccompanied] = useState(false);
  const [accompaniedPersonName, setAccompaniedPersonName] = useState('');
  const [paymentCurrency, setPaymentCurrency] = useState('USD');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [retrySeed, setRetrySeed] = useState(0);

  const paypalRef = useRef(null);
  const buttonsRef = useRef(null);
  const intentTokenRef = useRef(null);
  const mountedRef = useRef(true);

  const fees = conference?.fees || {};
  const baseCurrency = fees.currency || 'EUR';
  const { total: amount, currency: displayCurrency } = calculateFeeBreakdown({
    feeType,
    fees,
    accompaniedPersonsCount: withAccompanied ? 1 : 0,
    paymentCurrency
  });
  const conferenceId = conference ? conference._id : null;

  const safeSet = (setter) => (...args) => { if (mountedRef.current) setter(...args); };
  const safeSetError = safeSet(setError);
  const safeSetLoading = safeSet(setLoading);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!withAccompanied) setAccompaniedPersonName('');
  }, [withAccompanied]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Only load PayPal on step 2 (payment step)
      if (!conference || step !== 2) return;
      try {
        let activeClientId = clientId;
        if (!activeClientId) {
          const fetched = await fetchPayPalClientId();
          if (cancelled) return;
          if (!fetched) {
            safeSetError('PayPal client ID not returned from server');
            return;
          }
          setClientId(fetched);
          activeClientId = fetched;
        }

        const paypal = await loadPayPalSdk({ clientId: activeClientId, currency: paymentCurrency });
        if (cancelled || !paypalRef.current) return;

        if (buttonsRef.current) {
          try { buttonsRef.current.close?.(); } catch (_) {}
          paypalRef.current.innerHTML = '';
          buttonsRef.current = null;
        }

        const instance = paypal.Buttons({
          style: { layout: 'vertical', shape: 'rect' },
          createOrder: async () => {
            if (amount <= 0) {
              const msg = 'Calculated total is zero or invalid; please review selected fee.';
              safeSetError(msg);
              throw new Error(msg);
            }
            safeSetLoading(true);
            safeSetError('');
            successMessage && setSuccessMessage('');
            try {
              const order = await createRegistrationOrder({
                conferenceId,
                feeType,
                accompaniedPersonsCount: withAccompanied ? 1 : 0,
                accompaniedPersonName: withAccompanied ? accompaniedPersonName : undefined,
                paymentCurrency
              });
              intentTokenRef.current = order.intentToken;
              return order.orderId;
            } catch (err) {
              const msg = err.response?.data?.message || err.message || 'Failed to create order';
              safeSetError(msg);
              safeSetLoading(false);
              throw new Error(msg);
            }
          },
          onApprove: async (data) => {
            try {
              const capture = await captureRegistrationOrder({
                orderId: data.orderID,
                intentToken: intentTokenRef.current || undefined
              });
              safeSetLoading(false);
              setSuccessMessage('Payment confirmed and registration completed.');
              if (onSuccess) onSuccess(capture);
            } catch (err) {
              safeSetError(err.response?.data?.message || err.message || 'Capture failed');
              safeSetLoading(false);
            }
          },
          onError: (err) => {
            console.error('[PayPal Buttons onError]', err);
            safeSetError('Payment error: ' + (err?.message || 'Unknown error'));
            safeSetLoading(false);
          }
        });

        buttonsRef.current = instance;
        await instance.render(paypalRef.current);
      } catch (err) {
        if (!cancelled) safeSetError(err.message);
      }
    })();

    return () => {
      cancelled = true;
      if (buttonsRef.current) {
        try { buttonsRef.current.close?.(); } catch (_) {}
        buttonsRef.current = null;
      }
    };
  }, [step, conferenceId, feeType, withAccompanied, paymentCurrency, retrySeed]);

  if (!conference) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Attendee Registration {step === 2 && '- Payment'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <p><strong>Conference:</strong> {conference.title}</p>
            
            {step === 1 ? (
              // Step 1: Configuration
              <>
                <div className="mb-3">
                  <label className="form-label">Payment Currency</label>
                  <select 
                    className="form-select" 
                    value={paymentCurrency} 
                    onChange={(e) => setPaymentCurrency(e.target.value)}
                    disabled={step === 2}
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                  {paymentCurrency !== baseCurrency && (
                    <small className="text-muted d-block">
                      {paymentCurrency === 'USD' && baseCurrency === 'EUR' && (
                        <>Exchange rate: 1 EUR = {fees.exchangeRate || 1.1} USD</>
                      )}
                      {paymentCurrency === 'EUR' && baseCurrency === 'USD' && (
                        <>Exchange rate: 1 USD = {(1 / (fees.exchangeRate || 1.1)).toFixed(2)} EUR</>
                      )}
                    </small>
                  )}
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Select Fee Type</label>
                  <select className="form-select" value={feeType} onChange={(e) => setFeeType(e.target.value)}>
                    {feeTypes.map((f) => {
                      const { base } = calculateFeeBreakdown({ feeType: f.key, fees, accompaniedPersonsCount: 0, paymentCurrency });
                      return (
                        <option key={f.key} value={f.key}>{f.label} ({displayCurrency} {base})</option>
                      );
                    })}
                  </select>
                </div>
                
                <div className="mb-3">
                  <div className="form-check mb-2">
                    <input
                      type="checkbox"
                      id="withAccompanied"
                      className="form-check-input"
                      checked={withAccompanied}
                      onChange={(e) => setWithAccompanied(e.target.checked)}
                    />
                    <label htmlFor="withAccompanied" className="form-check-label">
                      Include Accompanied Person {`(+${displayCurrency} ${calculateFeeBreakdown({ feeType, fees, accompaniedPersonsCount: 1, paymentCurrency }).accompaniedFee})`}
                    </label>
                  </div>
                  {withAccompanied && (
                    <div className="mt-2">
                      <label className="form-label">Accompanied Person Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={accompaniedPersonName}
                        onChange={(e) => setAccompaniedPersonName(e.target.value)}
                        placeholder="Full name"
                      />
                      {!accompaniedPersonName.trim() && (
                        <small className="text-muted">Name is required to continue.</small>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="alert alert-info">
                  <strong>Total Amount:</strong> {displayCurrency} {amount}
                </div>
              </>
            ) : (
              // Step 2: Payment
              <>
                <div className="mb-3">
                  <p><strong>Fee Type:</strong> {feeType === 'student' ? 'Student' : 'Standard'}</p>
                  {withAccompanied && <p><strong>Includes:</strong> Accompanied Person</p>}
                  {withAccompanied && accompaniedPersonName.trim() && (
                    <p><strong>Accompanied Person:</strong> {accompaniedPersonName}</p>
                  )}
                  <p><strong>Currency:</strong> {displayCurrency}</p>
                  <p className="h5"><strong>Total:</strong> {displayCurrency} {amount}</p>
                </div>
                
                {error && (
                  <div className="alert alert-danger py-2 d-flex justify-content-between align-items-start">
                    <span>{error}</span>
                    <button className="btn btn-sm btn-outline-light" onClick={() => { safeSetError(''); setRetrySeed((s) => s + 1); }} disabled={loading}>Retry</button>
                  </div>
                )}
                {successMessage && (
                  <div className="alert alert-success py-2 d-flex justify-content-between align-items-start">
                    <span>{successMessage}</span>
                    <button className="btn btn-sm btn-outline-light" onClick={() => setSuccessMessage('')} disabled={loading}>Dismiss</button>
                  </div>
                )}
                {loading && <div className="small text-muted mb-2">Processing...</div>}
                <div ref={paypalRef}></div>
              </>
            )}
          </div>
          <div className="modal-footer">
            {step === 1 ? (
              <>
                <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button 
                  className="btn btn-primary" 
                  onClick={() => setStep(2)}
                  disabled={amount <= 0 || (withAccompanied && !accompaniedPersonName.trim())}
                >
                  Proceed to Payment
                </button>
              </>
            ) : (
              <>
                <button className="btn btn-secondary" onClick={() => setStep(1)} disabled={loading}>
                  Back
                </button>
                <button className="btn btn-secondary" onClick={onClose}>Close</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
